import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";
import Loading from "../../common/components/loading/loading";
import * as XLSX from "xlsx";
import { formatDate } from "../../common/services/utilities";

const SettlementsTable = ({
  settlements,
  isLoading,
  title,
  showHeatRate,
  pricePrecision,
  currencySymbol,
  effectiveDate,
}) => {
  const { t } = useTranslation(["marketdata"]);

  // Define cell renderers
  const renderDeltaCell = useCallback((params) => {
    const isPositive = params.data.current >= params.data.yesterday;
    return (
      <span
        style={{
          display: "inline-block",
          color: isPositive
            ? "var(--theme-green)"
            : "var(--theme-red)",
        }}
      >
        <FontAwesomeIcon
          icon={isPositive ? faSortUp : faSortDown}
          style={{
            fontSize: "1em",
            height: "1em",
            marginRight: 4,
          }}
        />{" "}
        {params.value}
      </span>
    );
  }, []);

  const renderHeatRateCell = useCallback((params) => {
    return <span style={{ color: "MediumPurple" }}>{params.value}</span>;
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    const baseCols = [
      {
        field: "termName",
        headerName: t("TermName"),
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "current",
        headerName: t("Current"),
        type: "rightAligned",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
      {
        field: "yesterdayDelta",
        headerName: t("YesterdayDelta"),
        type: "rightAligned",
        cellRenderer: renderDeltaCell,
        cellDataType: false,
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
      {
        field: "mtdAverage",
        headerName: t("MtdAverage"),
        type: "rightAligned",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
      {
        field: "ytdAverage",
        headerName: t("YtdAverage"),
        type: "rightAligned",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
      {
        field: "ytdLow",
        headerName: t("YtdLow"),
        sortable: true,
        filter: false,
        type: "rightAligned",
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
      {
        field: "ytdHigh",
        headerName: t("YtdHigh"),
        sortable: true,
        filter: false,
        type: "numericColumn",
        flex: 1,
        minWidth: 66,
        resizable: true,
      },
    ];

    if (showHeatRate) {
      baseCols.push({
        field: "heatRate",
        headerName: t("HeatRate"),
        cellRenderer: renderHeatRateCell,
        cellDataType: false,
        sortable: true,
        filter: false,
        type: "numericColumn",
        flex: 1,
        minWidth: 66,
        resizable: true,
      });
    }

    return baseCols;
  }, [t, showHeatRate, renderDeltaCell, renderHeatRateCell]);

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (settlements) => {
      return settlements.map((settlement) => ({
        termName: settlement.termName,
        current: currencySymbol + settlement.current.toFixed(pricePrecision),
        yesterday: settlement.yesterday.toFixed(pricePrecision),
        yesterdayDelta:
          currencySymbol +
          (settlement.current - settlement.yesterday).toFixed(pricePrecision),
        mtdAverage:
          currencySymbol + settlement.mtdAverage.toFixed(pricePrecision),
        ytdAverage:
          currencySymbol + settlement.ytdAverage.toFixed(pricePrecision),
        ytdLow: currencySymbol + settlement.ytdLow.toFixed(pricePrecision),
        ytdHigh: currencySymbol + settlement.ytdHigh.toFixed(pricePrecision),
        heatRate: settlement.heatRate ? settlement.heatRate.toFixed(2) : "",
      }));
    },
    [currencySymbol, pricePrecision]
  );

  // Export to Excel
  const downloadToExcel = useCallback(() => {
    const excelData = settlements.map((settlement) => ({
      termName: settlement.termName ?? null,
      beginDate: formatDate(settlement.beginDate) ?? null,
      endDate: formatDate(settlement.endDate) ?? null,
      current: settlement.current.toFixed(pricePrecision) ?? null,
      yesterday: settlement.yesterday.toFixed(pricePrecision) ?? null,
      yesterdayDelta:
        (settlement.current - settlement.yesterday).toFixed(pricePrecision) ??
        null,
      mtdAverage: settlement.mtdAverage.toFixed(pricePrecision) ?? null,
      ytdAverage: settlement.ytdAverage.toFixed(pricePrecision) ?? null,
      ytdLow: settlement.ytdLow.toFixed(pricePrecision) ?? null,
      ytdHigh: settlement.ytdHigh.toFixed(pricePrecision) ?? null,
      heatRate: settlement.heatRate
        ? settlement.heatRate.toFixed(2)
        : null ?? null,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, title + " " + formatDate(effectiveDate) + ".xlsx");
  }, [settlements, title, effectiveDate, pricePrecision]);

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback((params) => params.data.termName, []);

  if (isLoading) return <Loading />;

  const tableData = mapToViewModel(settlements);

  return (
    <div>
      <div className="row">
        <div className="col-md-10">
          <h6>{title}</h6>
        </div>
        <div className="col-md-2" style={{ alignSelf: "flex-end" }}>
          <button
            style={{ float: "right" }}
            className="btn btn-sm btn-primary no-print"
            onClick={downloadToExcel}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faDownload} /> {t("Excel")}
          </button>
        </div>
      </div>
      <ThemeableAgGrid
        className="table-extra-small settlement-table"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={tableData}
        getRowId={getRowId}
        domLayout="autoHeight"
        animateRows={true}
        allowHorizontalScroll={false}
      />
    </div>
  );
};

export default SettlementsTable;
